import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  BookOpenIcon,
  UsersIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarDaysIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import {
  getLibraryStats,
  getDashboardMetrics,
  type LibraryStats,
  type DashboardMetrics,
} from '@/api/libraryApi'
import toast from 'react-hot-toast'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts'

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

const LibraryDashboardPage: React.FC = () => {
  const { user } = useAuth()
  const [stats, setStats] = useState<LibraryStats | null>(null)
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const [statsRes, metricsRes] = await Promise.all([
        getLibraryStats(),
        getDashboardMetrics(),
      ])
      setStats(statsRes.data)
      setMetrics(metricsRes.data)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const statCards = [
    {
      title: 'Total Books',
      value: stats?.overview.totalBooks || 0,
      icon: BookOpenIcon,
      color: 'bg-blue-500',
      subtext: `${stats?.overview.totalCopies || 0} copies`,
    },
    {
      title: 'Available Copies',
      value: stats?.overview.availableCopies || 0,
      icon: ChartBarIcon,
      color: 'bg-green-500',
      subtext: `${stats?.overview.borrowedCopies || 0} borrowed`,
    },
    {
      title: 'Active Borrowings',
      value: stats?.overview.activeBorrowings || 0,
      icon: UsersIcon,
      color: 'bg-purple-500',
      subtext: `${metrics?.dueTodayCount || 0} due today`,
    },
    {
      title: 'Overdue Books',
      value: stats?.overview.overdueBorrowings || 0,
      icon: ExclamationTriangleIcon,
      color: 'bg-red-500',
      subtext: 'Needs attention',
    },
    {
      title: 'Pending Fines',
      value: `KES ${stats?.overview.pendingFines.amount?.toLocaleString() || 0}`,
      icon: CurrencyDollarIcon,
      color: 'bg-amber-500',
      subtext: `${stats?.overview.pendingFines.count || 0} pending`,
    },
    {
      title: 'Categories',
      value: stats?.overview.totalCategories || 0,
      icon: CalendarDaysIcon,
      color: 'bg-indigo-500',
      subtext: 'Book categories',
    },
  ]

  const todayStats = [
    { label: 'Books Borrowed', value: metrics?.todayBorrowings || 0, trend: 'up' },
    { label: 'Books Returned', value: metrics?.todayReturns || 0, trend: 'up' },
    { label: 'Due Today', value: metrics?.dueTodayCount || 0, trend: 'neutral' },
    { label: 'Fines Collected', value: `KES ${metrics?.finesCollectedToday?.toLocaleString() || 0}`, trend: 'up' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Library Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of library operations</p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
        >
          Refresh Data
        </button>
      </div>

      {/* Today's Quick Stats */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <h2 className="text-lg font-semibold mb-4">Today's Activity</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {todayStats.map((stat, index) => (
            <div key={index} className="bg-white/10 rounded-lg p-4">
              <p className="text-sm text-white/80">{stat.label}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-2xl font-bold">{stat.value}</span>
                {stat.trend === 'up' && <ArrowTrendingUpIcon className="w-5 h-5 text-green-300" />}
                {stat.trend === 'down' && <ArrowTrendingDownIcon className="w-5 h-5 text-red-300" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                <p className="text-xs text-gray-400 mt-1">{card.subtext}</p>
              </div>
              <div className={`${card.color} p-3 rounded-lg`}>
                <card.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Borrowing Trends */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Borrowing Trends (Last 30 Days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={stats?.borrowingTrends?.map((item, index) => ({
                  date: item._id.slice(5), // MM-DD format
                  borrowings: item.borrowings,
                  returns: stats?.returnTrends?.[index]?.returns || 0,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="borrowings" stroke="#3B82F6" strokeWidth={2} name="Borrowed" />
                <Line type="monotone" dataKey="returns" stroke="#10B981" strokeWidth={2} name="Returned" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Books by Category</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats?.categoryBreakdown?.map((cat) => ({
                    name: cat.name,
                    value: cat.bookCount,
                  }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {stats?.categoryBreakdown?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.colorCode || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Popular Books & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Books */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Popular Books</h3>
          <div className="space-y-4">
            {stats?.popularBooks?.slice(0, 5).map((item, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  {item.book.coverImage ? (
                    <img src={item.book.coverImage} alt="" className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <BookOpenIcon className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{item.book.title}</p>
                  <p className="text-xs text-gray-500">{item.book.authors?.join(', ')}</p>
                </div>
                <div className="flex-shrink-0">
                  <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                    {item.count} loans
                  </span>
                </div>
              </div>
            ))}
            {(!stats?.popularBooks || stats.popularBooks.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">No data available</p>
            )}
          </div>
        </div>

        {/* Recent Borrowings */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Borrowings</h3>
          <div className="space-y-4">
            {stats?.recentBorrowings?.slice(0, 5).map((borrowing, index) => {
              const book = typeof borrowing.book === 'object' ? borrowing.book : null
              const borrower = typeof borrowing.borrower === 'object' ? borrowing.borrower : null
              return (
                <div key={index} className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    {borrower?.profileImage ? (
                      <img src={borrower.profileImage} alt="" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <UsersIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {borrower ? `${borrower.firstName} ${borrower.lastName}` : 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{book?.title || 'Unknown Book'}</p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-gray-500">
                      {new Date(borrowing.borrowedDate).toLocaleDateString()}
                    </p>
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                        borrowing.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : borrowing.status === 'overdue'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {borrowing.status}
                    </span>
                  </div>
                </div>
              )
            })}
            {(!stats?.recentBorrowings || stats.recentBorrowings.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">No recent borrowings</p>
            )}
          </div>
        </div>
      </div>

      {/* Category Details Table */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Details</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Books</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Copies</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilization</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stats?.categoryBreakdown?.map((cat, index) => {
                const utilization = cat.totalCopies > 0
                  ? Math.round(((cat.totalCopies - cat.availableCopies) / cat.totalCopies) * 100)
                  : 0
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: cat.colorCode || COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm font-medium text-gray-900">{cat.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{cat.bookCount}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{cat.totalCopies}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{cat.availableCopies}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500 rounded-full"
                            style={{ width: `${utilization}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{utilization}%</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {(!stats?.categoryBreakdown || stats.categoryBreakdown.length === 0) && (
            <p className="text-sm text-gray-500 text-center py-8">No categories found</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default LibraryDashboardPage
